import * as React from 'react';
import * as moment from 'moment';
import * as _ from 'lodash';
import { Modal, Button, Alert } from 'react-bootstrap';
import { ILogisticsProvider, ITransportType, ILogisticsWithOption, ILogisticsOptionRequest, ILogisticsOption, IUser } from '@nkr/models/index';
import { LogisticsAudience, LogisticsOptionStatus, TransportMethod } from '@nkr/enums/index';
import LogisticsOptionsContent from './option-content';
import { ILogisticsOptionState, IOption } from './logistics-option-state';
import OptionNotes from './option-notes';
import { singleLegs, multipleLegs, charterLegs } from './leg-types';
import { timeZoneMap } from './time-zone-map';

enum SaveType {
    Draft = 1,
    Submit = 2,
    Return = 3
}

interface IProps {
    show: boolean,
    data: ILogisticsWithOption,
    logisticsProviders: ILogisticsProvider[],
    transportTypes: ITransportType[],
    audience: LogisticsAudience,
    user: IUser,
    onSwitchToSwapLogistics: (positionNumber: number) => void,
    onSave: (logistics: ILogisticsOptionRequest) => void,
    onDelete: (chainId: number, clusterNumber: number, position: number) => void,
    onReturn: (chainId: number, clusterNumber: number, position: number) => void,
    onCancel: () => void
}

interface IState {
    data: ILogisticsOptionState,
    allowedOptions: number,
    toggle: {
        show: boolean,
        type: string
    },
    dirty: boolean;
}

const initialOptionState: IOption = {
    index: 0,

    kidneyPickupDateTime: '',
    kidneyPickupDateTimeValid: true,
    kidneyPickupDateTimeBackup: '',
    kidneyPickupDateTimeBackupValid: true,
    kidneyPickupDateTimeTz: '',

    flightRouteOrGround: '',
    flightRouteOrGroundValid: true,
    flightRouteOrGroundBackup: '',
    flightRouteOrGroundBackupValid: true,

    kidneyArrivalDateTime: '',
    kidneyArrivalDateTimeValid: true,
    kidneyArrivalDateTimeBackup: '',
    kidneyArrivalDateTimeBackupValid: true,
    kidneyArrivalDateTimeTz: '',

    transportMethod: '',
    transportMethodValid: true,
    transportMethodBackup: '',
    transportMethodBackupValid: true,

    transportCost: '',
    transportCostValid: true,
    transportCostBackup: '',
    transportCostBackupValid: true,

    flightCarrierAndNumber: '',
    flightCarrierAndNumberValid: true,
    flightCarrierAndNumberBackup: '',
    flightCarrierAndNumberBackupValid: true,

    flightDepartureTime: '',
    flightDepartureTimeTz: '',
    flightDepartureTimeValid: true,
    flightDepartureTimeBackup: '',
    flightDepartureTimeBackupTz: '',
    flightDepartureTimeBackupValid: true,

    flightArrivalTime: '',
    flightArrivalTimeTz: '',
    flightArrivalTimeValid: true,
    flightArrivalTimeBackup: '',
    flightArrivalTimeBackupTz: '',
    flightArrivalTimeBackupValid: true,

    leg2DepartureTime: '',
    leg2DepartureTimeTz: '',
    leg2DepartureTimeValid: true,
    leg2DepartureTimeBackup: '',
    leg2DepartureTimeBackupTz: '',
    leg2DepartureTimeBackupValid: true,

    leg2ArrivalTime: '',
    leg2ArrivalTimeTz: '',
    leg2ArrivalTimeValid: true,
    leg2ArrivalTimeBackup: '',
    leg2ArrivalTimeBackupTz: '',
    leg2ArrivalTimeBackupValid: true,

    upCharge: '',
    upChargeValid: true,
    upChargeBackup: '',
    upChargeBackupValid: true,

    valid: true,
    missingBackup: false,
    missingPrimary: false,
    requiredOption: false,

    leg1BeforePickup: false,
    leg1ArrivalBeforeDeparture: false,
    leg1FlightsNotPaired: false,
    leg1ArrivalTooLate: false,
    leg2BeforeLeg1: false,
    leg2ArrivalBeforeDeparture: false,
    leg2FlightsNotPaired: false,
    leg2ArrivalTooLate: false,

    leg1BeforePickupBackup: false,
    leg1ArrivalBeforeDepartureBackup: false,
    leg1FlightsNotPairedBackup: false,
    leg1ArrivalTooLateBackup: false,
    leg2BeforeLeg1Backup: false,
    leg2ArrivalBeforeDepartureBackup: false,
    leg2FlightsNotPairedBackup: false,
    leg2ArrivalTooLateBackup: false,

    kidneyPickUpBeforeNow: false,
    arrivalPickUpOutOfRange: false,

    leg1FlightDepartureAfterArrival: false,

    leg1FlightArrivalAfterArrival: false,

    leg2DepartureBeforeLeg1: false,
    leg2DepatureBeforeLeg1Arrival: false,
    leg2DepatureAfterArival: false,

    leg2ArrivalBeforeLeg1: false,
    leg2ArrivalBeforeArrival: false,

    needKidneyDepartureArrival: false,

    backupKidneyPickUpBeforeNow: false,
    backupArrivalPickUpOutOfRange: false,

    backupFlightDepartureAfterArrival: false,

    backupLeg1ArrivalBeforeDeparture: false,
    backupLeg1FlightArrivalAfterArrival: false,

    backupLeg2DepartureBeforeLeg1: false,
    backupLeg2DepatureBeforeLeg1Arrival: false,
    backupLeg2DepatureAfterArival: false,

    backupLeg2ArrivalBeforeLeg1: false,

    backupLeg2ArrivalAfterArrival: false,

    backupNeedKidneyDepartureArrival: false,

    flightArrivalMissing: false,
    leg2ArrivalMissing: false,

    flightArrivalBackupMissing: false,
    leg2ArrivalBackupMissing: false,

    flightDepartureTimeTzMissing: false,
    flightArrivalTimeTzMissing: false,
    leg2DepartureTimeTzMissing: false,
    leg2ArrivalTimeTzMissing: false,

    backupflightDepartureTimeTzMissing: false,
    backupflightArrivalTimeTzMissing: false,
    backupleg2DepartureTimeTzMissing: false,
    backupleg2ArrivalTimeTzMissing: false,
};

const initialState: IState = {
    data: {
        logisticsProvider: '',
        jobNumber: '',
        deadline: '',
        deadlineValid: true,
        notes: [],
        options: _.range(0, 5).map(x => ({ ...initialOptionState, index: x }))
    },
    allowedOptions: 5,
    toggle: {
        show: false,
        type: ''
    },
    dirty: false
};

class LogisticsOption extends React.Component<IProps, IState> {
    readonly state: IState = initialState;

    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.show && this.props.show == false) {
            let { donorCenterTimezone, recipientCenterTimezone, logisticsProvider, list, kidneyPickupDateTime, kidneyArrivalDateTime, deadline, notes } = nextProps.data;

            let options: IOption[] = list.map((option, index) => {
                return {
                    index,

                    kidneyPickupDateTime: option.kidneyPickupDateTime,
                    kidneyPickupDateTimeValid: true,
                    kidneyPickupDateTimeBackup: option.kidneyPickupDateTimeBackup,
                    kidneyPickupDateTimeBackupValid: true,
                    kidneyPickupDateTimeTz: option.kidneyPickupDateTimeTz,

                    flightRouteOrGround: option.flightRouteOrGround,
                    flightRouteOrGroundValid: true,
                    flightRouteOrGroundBackup: option.flightRouteOrGroundBackup,
                    flightRouteOrGroundBackupValid: true,

                    kidneyArrivalDateTime: option.kidneyArrivalDateTime,
                    kidneyArrivalDateTimeValid: true,
                    kidneyArrivalDateTimeBackup: option.kidneyArrivalDateTimeBackup,
                    kidneyArrivalDateTimeBackupValid: true,
                    kidneyArrivalDateTimeTz: option.kidneyArrivalDateTimeTz,

                    transportMethod: option.transportMethod != null ? option.transportMethod.toString() : '',
                    transportMethodValid: true,
                    transportMethodBackup: option.transportMethodBackup != null ? option.transportMethodBackup.toString() : '',
                    transportMethodBackupValid: true,

                    transportCost: option.transportCost,
                    transportCostValid: true,
                    transportCostBackup: option.transportCostBackup,
                    transportCostBackupValid: true,

                    flightCarrierAndNumber: option.flightCarrierAndNumber,
                    flightCarrierAndNumberValid: true,
                    flightCarrierAndNumberBackup: option.flightCarrierAndNumberBackup,
                    flightCarrierAndNumberBackupValid: true,

                    flightDepartureTime: option.flightDepartureTime,
                    flightDepartureTimeTz: option.flightDepartureTimeTz,
                    flightDepartureTimeValid: true,
                    flightDepartureTimeBackup: option.flightDepartureTimeBackup,
                    flightDepartureTimeBackupTz: option.flightDepartureTimeBackupTz,
                    flightDepartureTimeBackupValid: true,

                    flightArrivalTime: option.flightArrivalTime,
                    flightArrivalTimeTz: option.flightArrivalTimeTz,
                    flightArrivalTimeValid: true,
                    flightArrivalTimeBackup: option.flightArrivalTimeBackup,
                    flightArrivalTimeBackupTz: option.flightArrivalTimeBackupTz,
                    flightArrivalTimeBackupValid: true,

                    leg2DepartureTime: option.flightLeg2DepartureTime,
                    leg2DepartureTimeTz: option.flightLeg2DepartureTimeTz,
                    leg2DepartureTimeValid: true,
                    leg2DepartureTimeBackup: option.flightLeg2DepartureTimeBackup,
                    leg2DepartureTimeBackupTz: option.flightLeg2DepartureTimeBackupTz,
                    leg2DepartureTimeBackupValid: true,

                    leg2ArrivalTime: option.flightLeg2ArrivalTime,
                    leg2ArrivalTimeTz: option.flightLeg2ArrivalTimeTz,
                    leg2ArrivalTimeValid: true,
                    leg2ArrivalTimeBackup: option.flightLeg2ArrivalTimeBackup,
                    leg2ArrivalTimeBackupTz: option.flightLeg2ArrivalTimeBackupTz,
                    leg2ArrivalTimeBackupValid: true,

                    upCharge: option.upCharge,
                    upChargeValid: true,
                    upChargeBackup: option.upChargeBackup,
                    upChargeBackupValid: true,

                    valid: true,
                    missingBackup: false,
                    missingPrimary: false,
                    requiredOption: false,

                    leg1BeforePickup: false,
                    leg1ArrivalBeforeDeparture: false,
                    leg1FlightsNotPaired: false,
                    leg1ArrivalTooLate: false,
                    leg2BeforeLeg1: false,
                    leg2ArrivalBeforeDeparture: false,
                    leg2FlightsNotPaired: false,
                    leg2ArrivalTooLate: false,

                    leg1BeforePickupBackup: false,
                    leg1ArrivalBeforeDepartureBackup: false,
                    leg1FlightsNotPairedBackup: false,
                    leg1ArrivalTooLateBackup: false,
                    leg2BeforeLeg1Backup: false,
                    leg2ArrivalBeforeDepartureBackup: false,
                    leg2FlightsNotPairedBackup: false,
                    leg2ArrivalTooLateBackup: false,

                    kidneyPickUpBeforeNow: false,
                    arrivalPickUpOutOfRange: false,

                    leg1FlightDepartureAfterArrival: false,

                    leg1FlightArrivalAfterArrival: false,

                    leg2DepartureBeforeLeg1: false,
                    leg2DepatureBeforeLeg1Arrival: false,
                    leg2DepatureAfterArival: false,

                    leg2ArrivalBeforeLeg1: false,
                    leg2ArrivalBeforeArrival: false,

                    needKidneyDepartureArrival: false,

                    backupKidneyPickUpBeforeNow: false,
                    backupArrivalPickUpOutOfRange: false,

                    backupFlightDepartureAfterArrival: false,

                    backupLeg1ArrivalBeforeDeparture: false,
                    backupLeg1FlightArrivalAfterArrival: false,

                    backupLeg2DepartureBeforeLeg1: false,
                    backupLeg2DepatureBeforeLeg1Arrival: false,
                    backupLeg2DepatureAfterArival: false,

                    backupLeg2ArrivalBeforeLeg1: false,

                    backupLeg2ArrivalAfterArrival: false,
                    backupNeedKidneyDepartureArrival: false,

                    flightArrivalMissing: false,
                    leg2ArrivalMissing: false,

                    flightArrivalBackupMissing: false,
                    leg2ArrivalBackupMissing: false,

                    flightDepartureTimeTzMissing: false,
                    flightArrivalTimeTzMissing: false,
                    leg2DepartureTimeTzMissing: false,
                    leg2ArrivalTimeTzMissing: false,

                    backupflightDepartureTimeTzMissing: false,
                    backupflightArrivalTimeTzMissing: false,
                    backupleg2DepartureTimeTzMissing: false,
                    backupleg2ArrivalTimeTzMissing: false,
                };
            });

            let missingOptionsCount = this.state.allowedOptions - options.length;

            for (let m = 0; m < missingOptionsCount; m++) {
                let initialOptionData = _.cloneDeep(initialOptionState);
                initialOptionData.kidneyPickupDateTime = kidneyPickupDateTime;
                initialOptionData.kidneyPickupDateTimeBackup = kidneyPickupDateTime;
                initialOptionData.kidneyPickupDateTimeTz = donorCenterTimezone;
                initialOptionData.kidneyArrivalDateTime = kidneyArrivalDateTime;
                initialOptionData.kidneyArrivalDateTimeBackup = kidneyArrivalDateTime;
                initialOptionData.kidneyArrivalDateTimeTz = recipientCenterTimezone;
                initialOptionData.index = options.length;

                options.push(initialOptionData);
            }

            this.setState({
                data: {
                    ...this.state.data,
                    logisticsProvider: logisticsProvider != null ? logisticsProvider : '',
                    deadline,
                    notes,
                    options
                },
                dirty: false
            });
        }
    }

    handleLogisticsProviderChange = (value: string) => {
        let data = _.cloneDeep({ ...this.state.data, logisticsProvider: value });

        this.setState({
            data
        });
    }

    handleDeadlineChange = (value: string) => {
        let data = { ...this.state.data, deadline: value, deadlineValid: value != '' ? moment(value).isValid() : false };

        this.setState({
            data
        });
    }

    handleDateTimeFieldChange = (option: number, field: string, e: string) => {
        let data = _.cloneDeep({ ...this.state.data });
        data.options[option][field] = e;

        if (this.state.dirty)
            data.options = this.validateOptions(data.options);

        this.setState({
            data
        });
    }

    handleFieldChange = (option: number, field: string, value: string) => {
        let data = _.cloneDeep({ ...this.state.data });
        data.options[option][field] = value;

        if (this.state.dirty) {
            data.options = this.validateOptions(data.options);
        }

        this.setState({
            data
        });
    }

    handleDropdownChange = (option: number, id: string, value: string) => {
        let data = _.cloneDeep({ ...this.state.data });
        data.options[option][id] = value;

        this.setState({
            data
        });
    }

    handleRemoveOption = (option: number) => {
        let options = _.cloneDeep(this.state.data.options.filter((plan, index) => index != option));
        let { kidneyPickupDateTime, kidneyArrivalDateTime, donorCenterTimezone, recipientCenterTimezone } = this.props.data;

        let missingOptionsCount = this.state.allowedOptions - options.length;

        for (let m = 0; m < missingOptionsCount; m++) {
            let initialOptionData = _.cloneDeep(initialOptionState);
            initialOptionData.kidneyPickupDateTime = kidneyPickupDateTime;
            initialOptionData.kidneyPickupDateTimeBackup = kidneyPickupDateTime;
            initialOptionData.kidneyPickupDateTimeTz = donorCenterTimezone;
            initialOptionData.kidneyArrivalDateTime = kidneyArrivalDateTime;
            initialOptionData.kidneyArrivalDateTimeBackup = kidneyArrivalDateTime;
            initialOptionData.kidneyArrivalDateTimeTz = recipientCenterTimezone;
            initialOptionData.index = options.length;

            options.push(initialOptionData);
        }

        this.setState({
            data: {
                ...this.state.data,
                options
            }
        });
    }

    handleClearOption = (option: number) => {
        let options = _.cloneDeep(this.state.data.options);
        let { kidneyPickupDateTime, kidneyArrivalDateTime, donorCenterTimezone, recipientCenterTimezone } = this.props.data;

        let initialOptionData = _.cloneDeep(initialOptionState);
        initialOptionData.kidneyPickupDateTime = kidneyPickupDateTime;
        initialOptionData.kidneyPickupDateTimeBackup = kidneyPickupDateTime;
        initialOptionData.kidneyPickupDateTimeTz = donorCenterTimezone;
        initialOptionData.kidneyArrivalDateTime = kidneyArrivalDateTime;
        initialOptionData.kidneyArrivalDateTimeBackup = kidneyArrivalDateTime;
        initialOptionData.kidneyArrivalDateTimeTz = recipientCenterTimezone;
        initialOptionData.index = option;

        options[option] = initialOptionData;

        this.setState({
            data: {
                ...this.state.data,
                options
            }
        });
    }

    handleAddNote = (notes: string) => {
        let data = _.cloneDeep({ ...this.state.data });
        data.notes.push({ logDateTime: moment().format('MM/DD/YY HH:mm'), notes, createdBy: `${this.props.user.firstName} ${this.props.user.lastName}`, userId: this.props.user.id });

        this.setState({
            data
        });
    }

    handleUpdateNote = (index: number, value: string) => {
        let data = _.cloneDeep({ ...this.state.data });

        data.notes[index].notes = value;
        data.notes[index].logDateTime = moment().format('MM/DD/YY HH:mm');

        this.setState({
            data
        });
    }

    handleDeleteNote = (index: number) => {
        let data = _.cloneDeep({ ...this.state.data });
        data.notes = data.notes.filter((item, noteIndex) => noteIndex != index);

        this.setState({
            data
        });
    }

    handleSave = (saveType: SaveType) => {
        let status = LogisticsOptionStatus.Draft;

        if (this.props.audience == LogisticsAudience.Courier && saveType == SaveType.Submit)
            status = LogisticsOptionStatus.SubmittedToNkr;

        if (this.props.audience == LogisticsAudience.NKR) {
            switch (saveType) {
                case SaveType.Draft:
                    status = LogisticsOptionStatus.NkrDraft;
                    break;

                case SaveType.Submit:
                    status = LogisticsOptionStatus.SubmittedToCenter;
                    break;
            }
        }

        let deadlineValid = this.props.audience == LogisticsAudience.NKR ? (this.state.data.deadline != '' ? moment(this.state.data.deadline).isValid() : false) : true;

        let options = this.validateOptions(_.cloneDeep(this.state.data.options));

        var request: ILogisticsOptionRequest = {
            chainId: this.props.data.chainId,
            clusterNumber: this.props.data.clusterNumber,
            positionNumber: this.props.data.position,
            recipientId: this.props.data.recipientId,
            donorId: this.props.data.donorId,
            logisticsProvider: this.state.data.logisticsProvider != '' ? parseInt(this.state.data.logisticsProvider) : null,
            deadline: this.state.data.deadline,
            notes: this.state.data.notes,
            options: options.map((plan, index) => {
                let {
                    kidneyPickupDateTime, kidneyPickupDateTimeBackup,
                    kidneyArrivalDateTime, kidneyArrivalDateTimeBackup,
                    transportMethod, transportMethodBackup,
                    transportCost, transportCostBackup,
                    flightRouteOrGround, flightRouteOrGroundBackup,
                    flightCarrierAndNumber, flightCarrierAndNumberBackup,
                    flightDepartureTime, flightDepartureTimeTz, flightDepartureTimeBackup, flightDepartureTimeBackupTz,
                    flightArrivalTime, flightArrivalTimeTz, flightArrivalTimeBackup, flightArrivalTimeBackupTz,
                    leg2DepartureTime, leg2DepartureTimeTz, leg2DepartureTimeBackup, leg2DepartureTimeBackupTz,
                    leg2ArrivalTime, leg2ArrivalTimeTz, leg2ArrivalTimeBackup, leg2ArrivalTimeBackupTz,
                    upCharge, upChargeBackup
                } = plan;

                if (this.props.audience == LogisticsAudience.Courier) {
                    if (parseInt(transportMethod) == TransportMethod.Charter || parseInt(transportMethod) == TransportMethod.CharterComm || parseInt(transportMethod) == TransportMethod.CommCharter) {
                        upCharge = transportCost != '' ? (parseFloat(transportCost) * 0.20).toFixed(2) : '0';
                    }

                    if (parseInt(transportMethodBackup) == TransportMethod.Charter || parseInt(transportMethodBackup) == TransportMethod.CharterComm || parseInt(transportMethodBackup) == TransportMethod.CommCharter) {
                        upChargeBackup = transportCostBackup != '' ? (parseFloat(transportCostBackup) * 0.20).toFixed(2) : '0';
                    }
                }

                let logisticsPlan: ILogisticsOption = {
                    id: 0,
                    chainId: this.props.data.chainId,
                    clusterNumber: this.props.data.clusterNumber,
                    positionNumber: this.props.data.position,
                    optionNumber: index,

                    kidneyPickupDateTime: kidneyPickupDateTime != '' ? moment(kidneyPickupDateTime).format('MM/DD/YY HH:mm') : null,
                    kidneyPickupDateTimeBackup: kidneyPickupDateTimeBackup != '' ? moment(kidneyPickupDateTimeBackup).format('MM/DD/YY HH:mm') : null,
                    kidneyPickupDateTimeTz: this.props.data.donorCenterTimezone,
                    kidneyArrivalDateTime: kidneyArrivalDateTime != '' ? moment(kidneyArrivalDateTime).format('MM/DD/YY HH:mm') : null,
                    kidneyArrivalDateTimeBackup: kidneyArrivalDateTimeBackup != '' ? moment(kidneyArrivalDateTimeBackup).format('MM/DD/YY HH:mm') : null,
                    kidneyArrivalDateTimeTz: this.props.data.recipientCenterTimezone,

                    flightRouteOrGround,
                    flightRouteOrGroundBackup,

                    transportMethod: transportMethod != '' ? parseInt(transportMethod) : null,
                    transportMethodBackup: transportMethodBackup != '' ? parseInt(transportMethodBackup) : null,

                    transportCost: transportCost,
                    transportCostBackup: transportCostBackup,

                    flightCarrierAndNumber,
                    flightCarrierAndNumberBackup,

                    flightDepartureTime: flightDepartureTime != '' ? moment(flightDepartureTime).format('MM/DD/YY HH:mm') : null,
                    flightDepartureTimeTz: flightDepartureTimeTz ? flightDepartureTimeTz : this.props.data.donorCenterTimezone,
                    flightDepartureTimeBackup: flightDepartureTimeBackup != '' ? moment(flightDepartureTimeBackup).format('MM/DD/YY HH:mm') : null,
                    flightDepartureTimeBackupTz: flightDepartureTimeBackupTz ? flightDepartureTimeBackupTz : this.props.data.donorCenterTimezone,

                    flightArrivalTime: flightArrivalTime != '' ? moment(flightArrivalTime).format('MM/DD/YY HH:mm') : null,
                    flightArrivalTimeTz: flightArrivalTimeTz ? flightArrivalTimeTz : this.props.data.recipientCenterTimezone,
                    flightArrivalTimeBackup: flightArrivalTimeBackup != '' ? moment(flightArrivalTimeBackup).format('MM/DD/YY HH:mm') : null,
                    flightArrivalTimeBackupTz: flightArrivalTimeBackupTz ? flightArrivalTimeBackupTz : this.props.data.recipientCenterTimezone,

                    flightLeg2DepartureTime: leg2DepartureTime != '' ? moment(leg2DepartureTime).format('MM/DD/YY HH:mm') : null,
                    flightLeg2DepartureTimeTz: leg2DepartureTimeTz ? leg2DepartureTimeTz : this.props.data.recipientCenterTimezone,
                    flightLeg2DepartureTimeBackup: leg2DepartureTimeBackup != '' ? moment(leg2DepartureTimeBackup).format('MM/DD/YY HH:mm') : null,
                    flightLeg2DepartureTimeBackupTz: leg2DepartureTimeBackupTz ? leg2DepartureTimeBackupTz : this.props.data.recipientCenterTimezone,

                    flightLeg2ArrivalTime: leg2ArrivalTime != '' ? moment(leg2ArrivalTime).format('MM/DD/YY HH:mm') : null,
                    flightLeg2ArrivalTimeTz: leg2ArrivalTimeTz ? leg2ArrivalTimeTz : this.props.data.recipientCenterTimezone,
                    flightLeg2ArrivalTimeBackup: leg2ArrivalTimeBackup != '' ? moment(leg2ArrivalTimeBackup).format('MM/DD/YY HH:mm') : null,
                    flightLeg2ArrivalTimeBackupTz: leg2ArrivalTimeBackupTz ? leg2ArrivalTimeBackupTz : this.props.data.recipientCenterTimezone,

                    upCharge,
                    upChargeBackup,

                    totalCost: 0,
                    totalCostBackup: 0,

                    status
                };

                return logisticsPlan;
            })
        };

        let valid = options.filter(opt => opt.valid == false).length == 0 && options.filter(opt => opt.transportMethod != '').length > 0;

        if ((valid && deadlineValid) || saveType == SaveType.Draft) {
            this.props.onSave(request);
        } else {
            this.setState({
                data: {
                    ...this.state.data,
                    options,
                    deadlineValid
                },
                dirty: true
            });
        }
    }

    handleCancel = () => {
        this.props.onCancel();
    }

    handleSwitchToSwapLogistics = () => {
        this.props.onSwitchToSwapLogistics(this.props.data.position);
    }

    handleToggle = (type: string) => {
        this.setState({
            toggle: {
                show: true,
                type
            }
        });
    }

    handleCancelToggle = () => {
        this.setState({
            toggle: {
                show: false,
                type: ''
            }
        });
    }

    handleProcessToggle = () => {
        let type = this.state.toggle.type;

        this.setState({
            toggle: {
                show: false,
                type: ''
            }
        }, () => {
            let { chainId, clusterNumber, position } = this.props.data;
            if (type == 'delete') {
                this.props.onDelete(chainId, clusterNumber, position);
            } else {
                this.props.onReturn(chainId, clusterNumber, position);
            }
        });
    }

    validateOptions = (options: IOption[]) => {
        let numberRegex = /^\s*[+-]?(\d+|\.\d+|\d+\.\d+|\d+\.)(e[+-]?\d+)?\s*$/;

        let p = options.map(x => {
            x.transportMethodValid = true;
            x.transportMethodBackupValid = true;

            x.flightRouteOrGroundValid = true;
            x.flightRouteOrGroundBackupValid = true;

            x.transportCostValid = true;
            x.transportCostBackupValid = true;

            x.upChargeValid = true;
            x.upChargeBackupValid = true;

            x.missingPrimary = false;
            x.missingBackup = false;
            x.requiredOption = false;

            x.leg1BeforePickup = false;
            x.leg1ArrivalBeforeDeparture = false;
            x.leg1FlightsNotPaired = false;
            x.leg1ArrivalTooLate = false;
            x.leg2BeforeLeg1 = false;
            x.leg2ArrivalBeforeDeparture = false;
            x.leg2FlightsNotPaired = false;
            x.leg2ArrivalTooLate = false;

            x.leg1BeforePickupBackup = false;
            x.leg1ArrivalBeforeDepartureBackup = false;
            x.leg1FlightsNotPairedBackup = false;
            x.leg1ArrivalTooLateBackup = false;
            x.leg2BeforeLeg1Backup = false;
            x.leg2ArrivalBeforeDepartureBackup = false;
            x.leg2FlightsNotPairedBackup = false;
            x.leg2ArrivalTooLateBackup = false;

            x.kidneyPickUpBeforeNow = false;
            x.arrivalPickUpOutOfRange = false;

            x.leg1FlightDepartureAfterArrival = false;

            x.leg1FlightArrivalAfterArrival = false;

            x.leg2DepartureBeforeLeg1 = false;
            x.leg2DepatureBeforeLeg1Arrival = false;
            x.leg2DepatureAfterArival = false;

            x.leg2ArrivalBeforeLeg1 = false;
            x.leg2ArrivalBeforeArrival = false;

            x.needKidneyDepartureArrival = false;

            x.backupKidneyPickUpBeforeNow = false;
            x.backupArrivalPickUpOutOfRange = false;

            x.backupFlightDepartureAfterArrival = false;

            x.backupLeg1ArrivalBeforeDeparture = false;
            x.backupLeg1FlightArrivalAfterArrival = false;

            x.backupLeg2DepartureBeforeLeg1 = false;
            x.backupLeg2DepatureBeforeLeg1Arrival = false;
            x.backupLeg2DepatureAfterArival = false;

            x.backupLeg2ArrivalBeforeLeg1 = false;

            x.backupLeg2ArrivalAfterArrival = false;
            x.backupNeedKidneyDepartureArrival = false;

            x.flightArrivalMissing = false;
            x.leg2ArrivalMissing = false;

            x.flightArrivalBackupMissing = false;
            x.leg2ArrivalBackupMissing = false;

            x.flightDepartureTimeTzMissing = false;
            x.flightArrivalTimeTzMissing = false;
            x.leg2DepartureTimeTzMissing = false;
            x.leg2ArrivalTimeTzMissing = false;

            x.backupflightDepartureTimeTzMissing = false;
            x.backupflightArrivalTimeTzMissing = false;
            x.backupleg2DepartureTimeTzMissing = false;
            x.backupleg2ArrivalTimeTzMissing = false;

            let {
                kidneyPickupDateTime, kidneyPickupDateTimeBackup,
                kidneyArrivalDateTime, kidneyArrivalDateTimeBackup,
                flightDepartureTime, flightDepartureTimeBackup,
                flightArrivalTime, flightArrivalTimeBackup,
                leg2DepartureTime, leg2DepartureTimeBackup,
                leg2ArrivalTime, leg2ArrivalTimeBackup
            } = x;

            let kidneyPickupTimeTz = timeZoneMap[x.kidneyPickupDateTimeTz];
            let flightDepartureTimeTz = timeZoneMap[x.flightDepartureTimeTz != '' ? x.flightDepartureTimeTz : x.kidneyPickupDateTimeTz];
            let flightDepartureTimeBackupTz = timeZoneMap[x.flightDepartureTimeBackupTz != '' ? x.flightDepartureTimeBackupTz : x.kidneyPickupDateTimeTz];
            let flightArrivalTimeTz = timeZoneMap[x.flightArrivalTimeTz != '' ? x.flightArrivalTimeTz : x.kidneyArrivalDateTimeTz];
            let flightArrivalTimeBackupTz = timeZoneMap[x.flightArrivalTimeBackupTz != '' ? x.flightArrivalTimeBackupTz : x.kidneyArrivalDateTimeTz];
            let leg2DepartureTimeTz = timeZoneMap[x.leg2DepartureTimeTz != '' ? x.leg2DepartureTimeTz : x.kidneyPickupDateTimeTz];
            let leg2DepartureTimeBackupTz = timeZoneMap[x.leg2DepartureTimeBackupTz != '' ? x.leg2DepartureTimeBackupTz : x.kidneyPickupDateTimeTz];
            let leg2ArrivalTimeTz = timeZoneMap[x.leg2ArrivalTimeTz != '' ? x.leg2ArrivalTimeTz : x.kidneyArrivalDateTimeTz];
            let leg2ArrivalTimeBackupTz = timeZoneMap[x.leg2ArrivalTimeBackupTz != '' ? x.leg2ArrivalTimeBackupTz : x.kidneyArrivalDateTimeTz];
            let kidneyArrivalTimeTz = timeZoneMap[x.kidneyArrivalDateTimeTz];

            x.kidneyPickupDateTimeValid = kidneyPickupDateTime ? moment(kidneyPickupDateTime, 'MM/DD/YY HH:mm').isValid() : true;
            x.kidneyPickupDateTimeBackupValid = kidneyPickupDateTimeBackup ? moment(kidneyPickupDateTimeBackup, 'MM/DD/YY HH:mm').isValid() : true;

            x.kidneyArrivalDateTimeValid = kidneyArrivalDateTime ? moment(kidneyArrivalDateTime, 'MM/DD/YY HH:mm').isValid() : true;
            x.kidneyArrivalDateTimeBackupValid = kidneyArrivalDateTimeBackup ? moment(kidneyArrivalDateTimeBackup, 'MM/DD/YY HH:mm').isValid() : true;

            x.flightDepartureTimeValid = flightDepartureTime ? moment(flightDepartureTime, 'MM/DD/YY HH:mm').isValid() : true;
            x.flightDepartureTimeBackupValid = flightDepartureTimeBackup ? moment(flightDepartureTimeBackup, 'MM/DD/YY HH:mm').isValid() : true;

            x.flightArrivalTimeValid = flightArrivalTime ? moment(flightArrivalTime, 'MM/DD/YY HH:mm').isValid() : true;
            x.flightArrivalTimeBackupValid = flightArrivalTimeBackup ? moment(flightArrivalTimeBackup, 'MM/DD/YY HH:mm').isValid() : true;

            x.leg2DepartureTimeValid = leg2DepartureTime ? moment(leg2DepartureTime, 'MM/DD/YY HH:mm').isValid() : true;
            x.leg2DepartureTimeBackupValid = leg2DepartureTimeBackup ? moment(leg2DepartureTimeBackup, 'MM/DD/YY HH:mm').isValid() : true;

            x.leg2ArrivalTimeValid = leg2ArrivalTime ? moment(leg2ArrivalTime, 'MM/DD/YY HH:mm').isValid() : true;
            x.leg2ArrivalTimeBackupValid = leg2ArrivalTimeBackup ? moment(leg2ArrivalTimeBackup, 'MM/DD/YY HH:mm').isValid() : true;

            let valid = true;

            let kidneyPickUpValid = kidneyPickupDateTime != '' && x.kidneyPickupDateTimeValid;
            let kidneyArrivalValid = kidneyArrivalDateTime != '' && x.kidneyArrivalDateTimeValid;
            let flightDepartureValid = flightDepartureTime != '' && x.flightDepartureTimeValid;
            let flightArrivalValid = flightArrivalTime != '' && x.flightArrivalTimeValid;
            let leg2DepartureValid = leg2DepartureTime != '' && x.leg2DepartureTimeValid;
            let leg2ArrivalValid = leg2ArrivalTime != '' && x.leg2ArrivalTimeValid;

            let kidneyPickUpBackupValid = kidneyPickupDateTimeBackup != '' && x.kidneyPickupDateTimeBackupValid;
            let kidneyArrivalBackupValid = kidneyArrivalDateTimeBackup != '' && x.kidneyArrivalDateTimeBackupValid;
            let flightDepartureBackupValid = flightDepartureTimeBackup != '' && x.flightDepartureTimeBackupValid;
            let flightArrivalBackupValid = flightArrivalTimeBackup != '' && x.flightArrivalTimeBackupValid;
            let leg2DepartureBackupValid = leg2DepartureTimeBackup != '' && x.leg2DepartureTimeBackupValid;
            let leg2ArrivalBackupValid = leg2ArrivalTimeBackup != '' && x.leg2ArrivalTimeBackupValid;

            let flightDepartureTimeTzIsValid = x.flightDepartureTimeTz != '';
            let flightArrivalTimeTzIsValid = x.flightArrivalTimeTz != '';
            let leg2DepartureTimeTzIsValid = x.leg2DepartureTimeTz != '';
            let leg2ArrivalTimeTzIsValid = x.leg2ArrivalTimeTz != '';
            let backupflightDepartureTimeTzIsValid = x.flightDepartureTimeBackupTz != '';
            let backupflightArrivalTimeTzIsValid = x.flightArrivalTimeBackupTz != '';
            let backupleg2DepartureTimeTzIsValid = x.leg2DepartureTimeBackupTz != '';
            let backupleg2ArrivalTimeTzIsValid = x.leg2ArrivalTimeBackupTz != '';

            if (kidneyPickupDateTime && x.kidneyPickupDateTimeValid && flightDepartureTime && x.flightDepartureTimeValid) {
                let kpdt = moment.tz(kidneyPickupDateTime, 'MM/DD/YY HH:mm', kidneyPickupTimeTz).utc();
                let fdt = moment.tz(flightDepartureTime, 'MM/DD/YY HH:mm', flightDepartureTimeTz).utc();

                if (fdt.isBefore(kpdt)) {
                    x.leg1BeforePickup = true;
                    valid = valid && false;
                }
            }

            if (kidneyPickupDateTimeBackup && x.kidneyPickupDateTimeBackupValid && flightDepartureTimeBackup && x.flightDepartureTimeBackupValid) {
                let kpdtb = moment.tz(kidneyPickupDateTimeBackup, 'MM/DD/YY HH:mm', kidneyPickupTimeTz).utc();
                let fdtb = moment(flightDepartureTimeBackup, 'MM/DD/YY HH:mm', flightDepartureTimeBackupTz).utc();

                if (fdtb.isBefore(kpdtb)) {
                    x.leg1BeforePickupBackup = true;
                    valid = valid && false;
                }
            }

            if (flightDepartureTime && x.flightDepartureTimeValid && flightArrivalTime && x.flightArrivalTimeValid) {
                let fdt = moment.tz(flightDepartureTime, 'MM/DD/YY HH:mm', flightDepartureTimeTz).utc();
                let fat = moment.tz(flightArrivalTime, 'MM/DD/YY HH:mm', flightArrivalTimeTz).utc();

                if (fat.isBefore(fdt)) {
                    x.leg1ArrivalBeforeDeparture = true;
                    valid = valid && false;
                }

                if (fat.isAfter(fdt.addDays(2))) {
                    x.leg1ArrivalTooLate = true;
                    valid = valid && false;
                }
            }

            if (flightDepartureTimeBackup && x.flightDepartureTimeBackupValid && flightArrivalTimeBackup && x.flightArrivalTimeBackupValid) {
                let fdtb = moment.tz(flightDepartureTimeBackup, 'MM/DD/YY HH:mm', flightDepartureTimeBackupTz).utc();
                let fatb = moment.tz(flightArrivalTimeBackup, 'MM/DD/YY HH:mm', flightArrivalTimeBackupTz).utc();

                if (fatb.isBefore(fdtb)) {
                    x.leg1ArrivalBeforeDepartureBackup = true;
                    valid = valid && false;
                }

                if (fatb.isAfter(fdtb.addDays(2))) {
                    x.leg1ArrivalTooLateBackup = true;
                    valid = valid && false;
                }
            }

            if (leg2DepartureTime && x.leg2DepartureTimeValid && leg2ArrivalTime && x.leg2ArrivalTimeValid) {
                let l2dt = moment.tz(leg2DepartureTime, 'MM/DD/YY HH:mm', leg2DepartureTimeTz).utc();
                let l2at = moment.tz(leg2ArrivalTime, 'MM/DD/YY HH:mm', leg2ArrivalTimeTz).utc();

                if (l2at.isBefore(l2dt)) {
                    x.leg2ArrivalBeforeDeparture = true;
                    valid = valid && false;
                }

                if (l2at.isAfter(l2dt.addDays(2))) {
                    x.leg2ArrivalTooLate = true;
                    valid = valid && false;
                }
            }

            if (leg2DepartureTimeBackup && x.leg2DepartureTimeBackupValid && leg2ArrivalTimeBackup && x.leg2ArrivalTimeBackupValid) {
                let l2dtb = moment.tz(leg2DepartureTimeBackup, 'MM/DD/YY HH:mm', leg2DepartureTimeBackupTz).utc();
                let l2atb = moment.tz(leg2ArrivalTimeBackup, 'MM/DD/YY HH:mm', leg2ArrivalTimeBackupTz).utc();

                if (l2atb.isBefore(l2dtb)) {
                    valid = valid && false;
                    x.leg2ArrivalBeforeDepartureBackup = true;
                }

                if (l2atb.isAfter(l2dtb.addDays(2))) {
                    x.leg2ArrivalTooLateBackup = true;
                    valid = valid && false;
                }
            }

            if (flightDepartureTime && x.flightDepartureTimeValid && flightArrivalTime && x.flightArrivalTimeValid && leg2DepartureTime && x.leg2DepartureTimeValid) {
                let fdt = moment.tz(flightDepartureTime, 'MM/DD/YY HH:mm', flightDepartureTimeTz).utc();
                let fat = moment.tz(flightArrivalTime, 'MM/DD/YY HH:mm', flightArrivalTimeTz).utc();
                let l2dt = moment.tz(leg2DepartureTime, 'MM/DD/YY HH:mm', leg2DepartureTimeTz).utc();

                if (l2dt.isBefore(fat) || l2dt.isBefore(fdt)) {
                    x.leg2BeforeLeg1 = true;
                    valid = valid && false;
                }
            }

            if (flightDepartureTimeBackup && x.flightDepartureTimeBackupValid && flightArrivalTimeBackup && x.flightArrivalTimeBackupValid && leg2DepartureTimeBackup && x.leg2DepartureTimeBackupValid) {
                let fdtb = moment.tz(flightDepartureTimeBackup, 'MM/DD/YY HH:mm', flightDepartureTimeBackupTz).utc();
                let fatb = moment.tz(flightArrivalTimeBackup, 'MM/DD/YY HH:mm', flightArrivalTimeBackupTz).utc();
                let l2dtb = moment.tz(leg2DepartureTimeBackup, 'MM/DD/YY HH:mm', leg2DepartureTimeBackupTz).utc();

                if (l2dtb.isBefore(fatb) || l2dtb.isBefore(fdtb)) {
                    x.leg2BeforeLeg1Backup = true;
                    valid = valid && false;
                }
            }

            let transportType = this.props.transportTypes.find(tt => tt.id == (x.transportMethod != '' ? parseInt(x.transportMethod) : 0));
            let transportTypeBackup = this.props.transportTypes.find(tt => tt.id == (x.transportMethodBackup != '' ? parseInt(x.transportMethodBackup) : 0));

            if (x.transportMethod != '') {
                let isSingleLeg = singleLegs.indexOf(transportType.description) >= 0;
                let isMultipleLeg = multipleLegs.indexOf(transportType.description) >= 0;
                let isCharter = charterLegs.indexOf(transportType.description) >= 0;
                let isGround = transportType.id == TransportMethod.Ground;

                if (isSingleLeg || isMultipleLeg || isGround) {
                    if (x.flightDepartureTime == '' || x.flightArrivalTime == '' || x.flightRouteOrGround == '') {
                        if (!isGround) {
                            if (x.flightDepartureTime == '') {
                                x.flightDepartureTimeValid = false;
                                valid = valid && false;
                            }

                            if (x.flightArrivalTime == '') {
                                x.flightArrivalTimeValid = false;
                                valid = valid && false;
                            }
                        }

                        if (x.flightRouteOrGround == '') {
                            x.flightRouteOrGroundValid = false;
                            valid = valid && false;
                        }

                        if ((x.flightDepartureTime != '' && x.flightArrivalTime == '') || (x.flightDepartureTime == '' && x.flightArrivalTime != '')) {
                            x.leg1FlightsNotPaired = true;
                            valid = valid && false;
                        }
                    }

                    if (isMultipleLeg) {
                        if (x.leg2DepartureTime == '' || x.leg2ArrivalTime == '') {
                            if (x.leg2DepartureTime == '') {
                                x.leg2DepartureTimeValid = false;
                                valid = valid && false;
                            }

                            if (x.leg2ArrivalTime == '') {
                                x.leg2ArrivalTimeValid = false;
                                valid = valid && false;
                            }

                            if ((x.leg2DepartureTime != '' && x.leg2ArrivalTime == '') || (x.leg2DepartureTime == '' && x.leg2ArrivalTime != '')) {
                                x.leg2FlightsNotPaired = true;
                                valid = valid && false;
                            }
                        }
                    }
                }

                if (isCharter) {
                    if (x.transportCost == '' || numberRegex.test(x.transportCost) == false) {
                        valid = valid && false;
                        x.transportCostValid = false;
                    }
                }

                if (x.upCharge != '' && numberRegex.test(x.upCharge) == false) {
                    valid = valid && false;
                    x.upChargeValid = false;
                }

                if (!kidneyPickUpValid || !kidneyArrivalValid) {
                    x.needKidneyDepartureArrival = true;
                    valid = valid && false;
                } else {
                    let kpdt = moment.tz(kidneyPickupDateTime, 'MM/DD/YY HH:mm', kidneyPickupTimeTz).utc();
                    let kadt = moment.tz(kidneyArrivalDateTime, 'MM/DD/YY HH:mm', kidneyArrivalTimeTz).utc();
                    let now = moment().utc();

                    if (kpdt.isBefore(now)) {
                        // must be in the future
                        x.kidneyPickUpBeforeNow = true;
                        valid = valid && false;
                    }

                    if (kadt.isBefore(kpdt) || kadt.isAfter(kpdt.addDays(2))) {
                        // not within range of 48 hours max after pick up date
                        x.arrivalPickUpOutOfRange = true;
                        valid = valid && false;
                    }

                    if (flightDepartureValid) {
                        let fdt = moment.tz(flightDepartureTime, 'MM/DD/YY HH:mm', flightDepartureTimeTz).utc();

                        //If timezone is missing, then throw error
                        if (!flightDepartureTimeTzIsValid) {
                            x.flightDepartureTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightArrivalValid) {
                            //new property missing arrival
                            x.flightArrivalMissing = true;
                            valid = valid && false;
                        } else if (fdt.isAfter(kadt)) {
                            //new property not in range of Arrival
                            x.leg1FlightDepartureAfterArrival = true;
                            valid = valid && false;
                        }
                    }

                    if (flightArrivalValid) {
                        //already checking in previous logic to see if arrival is after departure and within 48 hour grace period
                        let fat = moment.tz(flightArrivalTime, 'MM/DD/YY HH:mm', flightArrivalTimeTz).utc();

                        //If timezone is missing, then throw error
                        if (!flightArrivalTimeTzIsValid) {
                            x.flightArrivalTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightDepartureValid) {
                            //cant have arrival without departure
                            x.leg1ArrivalBeforeDeparture = true;
                            valid = valid && false;
                        } else if (fat.isAfter(kadt)) {
                            //new property not in range of Arrival
                            x.leg1FlightArrivalAfterArrival = true;
                            valid = valid && false;
                        }
                    }

                    if (leg2DepartureValid) {
                        //already checking in previous logic to see if before leg 1 
                        let l2dt = moment.tz(leg2DepartureTime, 'MM/DD/YY HH:mm', leg2DepartureTimeTz).utc();

                        //If timezone is missing, then throw error
                        if (!leg2DepartureTimeTzIsValid) {
                            x.leg2DepartureTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightDepartureValid || !flightArrivalValid) {
                            //cant have leg 2 departure without leg 1 
                            x.leg2DepatureBeforeLeg1Arrival = true;
                            valid = valid && false;
                        } else if (!leg2ArrivalValid) {
                            // new property missing leg 2 arrival
                            x.leg2ArrivalMissing = true;
                            valid = valid && false;
                        } else if (l2dt.isAfter(kadt)) {
                            //new property not in range of kidney arrival
                            x.leg2DepatureAfterArival = true;
                            valid = valid && false;
                        }
                    }

                    if (leg2ArrivalValid) {
                        let l2at = moment.tz(leg2ArrivalTime, 'MM/DD/YY HH:mm', leg2ArrivalTimeTz).utc();

                        //If timezone is missing, then throw error
                        if (!leg2ArrivalTimeTzIsValid) {
                            x.leg2ArrivalTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightDepartureValid || !flightArrivalValid || !leg2DepartureValid) {
                            //cant have leg 2 arrival without leg 1 and leg 2 arrival
                            x.leg2ArrivalBeforeLeg1 = true;
                            valid = valid && false;
                        } else if (l2at.isAfter(kadt)) {
                            //new property not in range of kidney arrival
                            x.leg2ArrivalBeforeArrival = true;
                            valid = valid && false;
                        } else {
                            //check if arrival date is before departure
                            let l2dt = moment.tz(leg2DepartureTime, 'MM/DD/YY HH:mm', leg2DepartureTimeTz).utc();

                            if (l2at.isBefore(l2dt)) {
                                x.leg2ArrivalBeforeDeparture = true;
                                valid = valid && false;
                            }

                            // if (l2at.isAfter(l2dt.addDays(2))) {
                            //     x.leg2ArrivalTooLate = true;
                            //     valid = valid && false;
                            // }
                        }
                    }
                }
            } else {
                valid = valid && true;
            }

            if (x.transportMethodBackup != '') {
                let isSingleLeg = singleLegs.indexOf(transportTypeBackup.description) >= 0;
                let isMultipleLeg = multipleLegs.indexOf(transportTypeBackup.description) >= 0;
                let isCharter = charterLegs.indexOf(transportTypeBackup.description) >= 0;
                let isGround = transportTypeBackup.id == TransportMethod.Ground;

                if (isSingleLeg || isMultipleLeg || isGround) {
                    if (x.flightDepartureTimeBackup == '' || x.flightArrivalTimeBackup == '' || x.flightRouteOrGroundBackup == '') {
                        if (!isGround) {
                            if (x.flightDepartureTimeBackup == '') {
                                x.flightDepartureTimeBackupValid = false;
                                valid = valid && false;
                            }

                            if (x.flightArrivalTimeBackup == '') {
                                x.flightArrivalTimeBackupValid = false;
                                valid = valid && false;
                            }
                        }

                        if (x.flightRouteOrGroundBackup == '') {
                            x.flightRouteOrGroundBackupValid = false;
                            valid = valid && false;
                        }

                        if ((x.flightDepartureTimeBackup != '' && x.flightArrivalTimeBackup == '') || (x.flightDepartureTimeBackup == '' && x.flightArrivalTimeBackup != '')) {
                            x.leg1FlightsNotPairedBackup = true;
                            valid = valid && false;
                        }
                    }

                    if (isMultipleLeg) {
                        if (x.leg2DepartureTimeBackup == '' || x.leg2ArrivalTimeBackup == '') {
                            if (x.leg2DepartureTimeBackup == '') {
                                x.leg2DepartureTimeBackupValid = false;
                                valid = valid && false;
                            }

                            if (x.leg2ArrivalTimeBackup == '') {
                                x.leg2ArrivalTimeBackupValid = false;
                                valid = valid && false;
                            }

                            if ((x.leg2DepartureTimeBackup != '' && x.leg2ArrivalTimeBackup == '') || (x.leg2DepartureTimeBackup == '' && x.leg2ArrivalTimeBackup != '')) {
                                x.leg2FlightsNotPairedBackup = true;
                                valid = valid && false;
                            }
                        }
                    }
                }

                if (isCharter) {
                    if (x.transportCostBackup == '' || numberRegex.test(x.transportCostBackup) == false) {
                        x.transportCostBackupValid = false;
                        valid = valid && false;
                    }
                }

                if (x.upChargeBackup != '' && numberRegex.test(x.upChargeBackup) == false) {
                    x.upChargeBackupValid = false;
                    valid = valid && false;
                }

                if (x.transportMethod == '') {
                    x.missingPrimary = true;
                    x.transportMethodValid = false;
                    valid = valid && false;
                }

                if (!kidneyPickUpBackupValid || !kidneyArrivalBackupValid) {
                    x.backupNeedKidneyDepartureArrival = true;
                    valid = valid && false;
                } else {
                    let kpbdt = moment.tz(kidneyPickupDateTimeBackup, 'MM/DD/YY HH:mm', kidneyPickupTimeTz).utc();
                    let kabdt = moment.tz(kidneyArrivalDateTimeBackup, 'MM/DD/YY HH:mm', kidneyArrivalTimeTz).utc();
                    let now = moment().utc()

                    if (kpbdt.isBefore(now)) {
                        // must be in the future
                        x.backupKidneyPickUpBeforeNow = true;
                        valid = valid && false;
                    }

                    if (kabdt.isBefore(kpbdt) || kabdt.isAfter(kpbdt.addDays(2))) {
                        // not within range of 48 hours max after pick up date
                        x.backupArrivalPickUpOutOfRange = true;
                        valid = valid && false;
                    }

                    if (flightDepartureBackupValid) {
                        //already checking in previous code if before kidney pick up
                        let fdbt = moment.tz(flightDepartureTimeBackup, 'MM/DD/YY HH:mm', flightDepartureTimeBackupTz).utc();

                        //If timezone is missing, then throw error
                        if (!backupflightDepartureTimeTzIsValid) {
                            x.backupflightDepartureTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightArrivalBackupValid) {
                            //new property missing arrival back up
                            x.flightArrivalBackupMissing = true;
                            valid = valid && false;
                        } else if (fdbt.isAfter(kabdt)) {
                            //new property not in range of Arrival
                            x.backupFlightDepartureAfterArrival = true;
                            valid = valid && false;
                        }
                    }

                    if (flightArrivalBackupValid) {
                        let fabt = moment.tz(flightArrivalTimeBackup, 'MM/DD/YY HH:mm', flightArrivalTimeBackupTz).utc();

                        //If timezone is missing, then throw error
                        if (!backupflightArrivalTimeTzIsValid) {
                            x.backupflightArrivalTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightDepartureBackupValid) {
                            //already checking in previous code if before departure or too late from departure
                            x.leg1ArrivalBeforeDepartureBackup = true;
                            valid = valid && false;
                        } else if (fabt.isAfter(kabdt)) {
                            //new property not in range of Arrival
                            x.backupLeg1FlightArrivalAfterArrival = true;
                            valid = valid && false;
                        }
                    }

                    if (leg2DepartureBackupValid) {
                        let l2dbt = moment.tz(leg2DepartureTimeBackup, 'MM/DD/YY HH:mm', leg2DepartureTimeBackupTz).utc();

                        //already checking in previous code if before leg 2
                        //If timezone is missing, then throw error
                        if (!backupleg2DepartureTimeTzIsValid) {
                            x.backupleg2DepartureTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightDepartureBackupValid || !flightArrivalBackupValid) {
                            //cant have leg 2 without arrival and departure
                            x.backupLeg2DepartureBeforeLeg1 = true;
                            valid = false && false;
                        } else if (!leg2ArrivalBackupValid) {
                            //new property leg2 arrival missing = true
                            x.leg2ArrivalBackupMissing = true;
                            valid = valid && false;
                        } else if (l2dbt.isAfter(kabdt)) {
                            //new property not in range of kidney arrival
                            x.backupLeg2DepatureAfterArival = true;
                            valid = valid && false;
                        }
                    }

                    if (leg2ArrivalBackupValid) {
                        let l2abt = moment.tz(leg2ArrivalTimeBackup, 'MM/DD/YY HH:mm', leg2ArrivalTimeBackupTz).utc();

                        if (!backupleg2ArrivalTimeTzIsValid) {
                            x.backupleg2ArrivalTimeTzMissing = true;
                            valid = valid && false;
                        } else if (!flightDepartureBackupValid || !flightArrivalBackupValid || !leg2DepartureBackupValid) {
                            //cant have leg 2 without leg 1 and leg 2 departure
                            x.backupLeg2ArrivalBeforeLeg1 = true;
                            valid = valid && false;
                        } else if (l2abt.isAfter(kabdt)) {
                            //new property not in range of kidney arrival
                            x.backupLeg2ArrivalAfterArrival = true;
                            valid = valid && false;
                        } else {
                            //check to see if its before leg 2 departure
                            let l2dbt = moment.tz(leg2DepartureTimeBackup, 'MM/DD/YY HH:mm', leg2DepartureTimeBackupTz).utc();

                            if (l2abt.isBefore(l2dbt)) {
                                x.leg2ArrivalBeforeDepartureBackup = true;
                                valid = valid && false;
                            }

                            // if (l2abt.isAfter(l2dbt.addDays(2))) {
                            //     x.leg2ArrivalTooLateBackup = true;
                            //     valid = valid && false;
                            // }
                        }
                    }

                }
            } else {
                if (x.transportMethod != '') {
                    let isGroundOrCharter = charterLegs.indexOf(transportType.description) >= 0 || transportType.id == TransportMethod.Ground;

                    valid = valid && isGroundOrCharter;

                    x.missingBackup = !isGroundOrCharter;
                    x.transportMethodBackupValid = isGroundOrCharter;
                } else {
                    valid = valid && true;
                }
            }

            x.valid = valid;

            return x;
        });

        if (p.filter(x => x.transportMethod != '').length == 0) {
            p[0].valid = false;
            p[0].transportMethodValid = false;
        }

        let firstPageIndexes = p.filter(x => x.index <= 2 && x.transportMethod == '').map(x => x.index);
        let remainingIndexes = p.filter(x => x.index > 2 && x.transportMethod != '').map(x => x.index);

        if (firstPageIndexes.length > 0 && remainingIndexes.length > 0) {
            p = p.map(option => {
                if (firstPageIndexes.indexOf(option.index) >= 0) {
                    option.requiredOption = true;
                    option.valid = false;
                }

                return option;
            });
        }

        return p;
    }

    renderErrors = () => {
        return (
            <>
                {this.state.data.options.filter(x => x.valid == false).map(option =>
                    <>
                        {option.requiredOption && (
                            <li>Option {option.index + 1} - must be specified</li>
                        )}

                        {option.missingPrimary && (
                            <li>Option {option.index + 1} - missing the primary</li>
                        )}

                        {option.missingBackup && (
                            <li>Option {option.index + 1} - missing the backup</li>
                        )}

                        {(option.needKidneyDepartureArrival) && (
                            <li>Option {option.index + 1} - primary kidney pick up and kidney arrival are required. </li>
                        )}

                        {(option.backupNeedKidneyDepartureArrival) && (
                            <li>Option {option.index + 1} - backup kidney pick up and kidney arrival are required. </li>
                        )}

                        {(option.kidneyPickUpBeforeNow || option.arrivalPickUpOutOfRange) && (
                            <li>Option {option.index + 1} - primary kidney pick up and kidney arrival must be in the future and within 48 hours range. </li>
                        )}

                        {(option.backupKidneyPickUpBeforeNow || option.backupArrivalPickUpOutOfRange) && (
                            <li>Option {option.index + 1} - backup kidney pick up and kidney arrival must be in the future and within 48 hours range. </li>
                        )}

                        {!option.flightRouteOrGroundValid && (
                            <li>Option {option.index + 1} - primary flight route or ground is required</li>
                        )}

                        {!option.flightRouteOrGroundBackupValid && (
                            <li>Option {option.index + 1} - backup flight route or ground is required</li>
                        )}

                        {!option.transportCostValid && (
                            <li>Option {option.index + 1} - primary transport cost is invalid; must be specified as a number, for example: 1000.00</li>
                        )}

                        {!option.transportCostBackupValid && (
                            <li>Option {option.index + 1} - backup transport cost is invalid; must be specified as a number, for example: 1000.00</li>
                        )}

                        {(option.leg1FlightsNotPaired || !option.flightDepartureTimeValid || !option.flightArrivalTimeValid) && (
                            <li>Option {option.index + 1} - primary needs to have both departure and arrival times</li>
                        )}

                        {(option.leg2FlightsNotPaired || !option.leg2DepartureTimeValid || !option.leg2ArrivalTimeValid) && (
                            <li>Option {option.index + 1} - primary needs to have both departure and arrival times for leg 2</li>
                        )}

                        {option.leg1BeforePickup && (
                            <li>Option {option.index + 1} - primary leg 1 departure should not be scheduled before pickup</li>
                        )}

                        {option.leg1FlightDepartureAfterArrival && (
                            <li>Option {option.index + 1} - primary leg 1 departure should not be scheduled after kidney arrival</li>
                        )}

                        {option.leg1ArrivalBeforeDeparture && (
                            <li>Option {option.index + 1} - primary leg 1 departure should not be scheduled after leg 1 arrival</li>
                        )}

                        {option.leg1FlightArrivalAfterArrival && (
                            <li>Option {option.index + 1} - primary leg 1 arrival should not be scheduled after kidney arrival</li>
                        )}

                        {(option.leg2DepartureBeforeLeg1 || option.leg2DepatureBeforeLeg1Arrival) && (
                            <li>Option {option.index + 1} - primary leg 2 departure should not be scheduled before leg 1 arrival</li>
                        )}

                        {option.leg2DepatureAfterArival && (
                            <li>Option {option.index + 1} - primary leg 2 departure should not be scheduled after kidney arrival</li>
                        )}

                        {(option.leg2ArrivalBeforeLeg1) && (
                            <li>Option {option.index + 1} - primary leg 2 arrival should not be scheduled before leg 2</li>
                        )}

                        {(option.leg2ArrivalBeforeDeparture) && (
                            <li>Option {option.index + 1} - primary leg 2 arrival should not be scheduled before leg 2 departure</li>
                        )}

                        {(option.leg2ArrivalBeforeArrival) && (
                            <li>Option {option.index + 1} - primary leg 2 arrival should not be scheduled after kidney arrival</li>
                        )}

                        {option.leg2BeforeLeg1 && (
                            <li>Option {option.index + 1} - primary leg 1 departure should not be scheduled after leg 2</li>
                        )}

                        {option.leg1ArrivalTooLate && (
                            <li>Option {option.index + 1} - primary leg 1 arrival should not be scheduled more than 2 days from leg 1 departure</li>
                        )}

                        {option.leg2ArrivalTooLate && (
                            <li>Option {option.index + 1} - primary leg 2 arrival should not be scheduled more than 2 days from leg 2 departure</li>
                        )}

                        {(option.leg1FlightsNotPairedBackup || !option.flightDepartureTimeBackupValid || !option.flightArrivalTimeBackupValid) && (
                            <li>Option {option.index + 1} - backup needs to have both departure and arrival times</li>
                        )}

                        {(option.leg2FlightsNotPairedBackup || !option.leg2DepartureTimeBackupValid || !option.leg2ArrivalTimeBackupValid) && (
                            <li>Option {option.index + 1} - backup needs to have both departure and arrival times for leg 2</li>
                        )}

                        {option.leg1BeforePickupBackup && (
                            <li>Option {option.index + 1} - backup leg 1 departure should not be scheduled before pickup</li>
                        )}

                        {option.backupFlightDepartureAfterArrival && (
                            <li>Option {option.index + 1} - backup leg 1 departure should not be scheduled after kidney arrival</li>
                        )}

                        {option.leg1ArrivalBeforeDepartureBackup && (
                            <li>Option {option.index + 1} - backup leg 1 arrival should not be scheduled before leg 1 departure</li>
                        )}

                        {option.leg2BeforeLeg1Backup && (
                            <li>Option {option.index + 1} - backup leg 1 departure should not be scheduled after leg 2</li>
                        )}

                        {option.leg1ArrivalTooLateBackup && (
                            <li>Option {option.index + 1} - backup leg 1 arrival should not be scheduled more than 2 days from backup leg 1 departure</li>
                        )}

                        {option.backupLeg1FlightArrivalAfterArrival && (
                            <li>Option {option.index + 1} - backup leg 1 arrival should not be scheduled after kidney arrival</li>
                        )}

                        {(option.backupLeg2DepartureBeforeLeg1 || option.backupLeg2DepatureBeforeLeg1Arrival) && (
                            <li>Option {option.index + 1} - backup leg 2 arrival should not be scheduled before leg 2 departure</li>
                        )}

                        {option.backupLeg2DepatureAfterArival && (
                            <li>Option {option.index + 1} - backup leg 2 arrival should not be scheduled after kidney arrival</li>
                        )}

                        {(option.leg2ArrivalBeforeDepartureBackup || option.backupLeg2ArrivalBeforeLeg1) && (
                            <li>Option {option.index + 1} - backup leg 2 arrival should not be scheduled before leg 2 departure</li>
                        )}

                        {option.leg2ArrivalTooLateBackup && (
                            <li>Option {option.index + 1} - backup leg 2 arrival should not be scheduled more than 2 days from backup leg 2 departure</li>
                        )}


                        {option.backupLeg2ArrivalAfterArrival && (
                            <li>Option {option.index + 1} - backup leg 2 arrival should not be scheduled after kidney arrival</li>
                        )}

                        {option.flightArrivalMissing && (
                            <li>Option {option.index + 1} - primary leg 1 arrival missing</li>
                        )}

                        {option.leg2ArrivalMissing && (
                            <li>Option {option.index + 1} - primary leg 2 arrival missing</li>
                        )}

                        {option.flightArrivalBackupMissing && (
                            <li>Option {option.index + 1} - back up leg 1 arrival missing</li>
                        )}

                        {option.leg2ArrivalBackupMissing && (
                            <li>Option {option.index + 1} - back up leg 2 arrival missing</li>
                        )}

                        {option.flightDepartureTimeTzMissing && (
                            <li>Option {option.index + 1} - primary leg 1 departure timezone missing</li>
                        )}

                        {option.flightArrivalTimeTzMissing && (
                            <li>Option {option.index + 1} - primary leg 1 arrival timezone missing</li>
                        )}

                        {option.leg2DepartureTimeTzMissing && (
                            <li>Option {option.index + 1} - primary leg 2 departure timezone missing</li>
                        )}

                        {option.leg2ArrivalTimeTzMissing && (
                            <li>Option {option.index + 1} - primary leg 2 arrival timezone missing</li>
                        )}

                        {option.backupflightDepartureTimeTzMissing && (
                            <li>Option {option.index + 1} - back up leg 1 departure timezone missing</li>
                        )}

                        {option.backupflightArrivalTimeTzMissing && (
                            <li>Option {option.index + 1} - back up leg 1 arrival timezone missing</li>
                        )}

                        {option.backupleg2DepartureTimeTzMissing && (
                            <li>Option {option.index + 1} - back up leg 2 departure timezone missing</li>
                        )}

                        {option.backupleg2ArrivalTimeTzMissing && (
                            <li>Option {option.index + 1} - back up leg 2 arrival timezone missing</li>
                        )}
                    </>
                )}
            </>
        );
    }

    getToggleType = () => {
        if (this.state.toggle.type == 'delete') {
            return { label: 'Yes, Delete Options', copy: 'delete all challenging logistics options for this swap' };
        }

        return { label: 'Yes, Return Options', copy: 'return the challenging logistics options to the courier for this swap' };
    }

    render = () => {
        if (this.props.data == null)
            return null;

        const position = this.props.data != null ? this.props.data.position : 0;
        const swapNumber = this.props.data != null ? `${this.props.data.chainId}/${this.props.data.clusterNumber}/${position}` : '';
        const donorCenter = this.props.data != null ? this.props.data.donorCenter : '';
        const donorAlias = this.props.data != null ? this.props.data.donorAlias : '';
        const rdnCenterId = this.props.data != null ? this.props.data.rdnCenterId : 0;
        const rdnCenterAlias = this.props.data != null ? this.props.data.rdnCenterAlias : '';
        const recipientCenter = this.props.data != null ? this.props.data.recipientCenter : '';
        const recipientAlias = this.props.data != null ? this.props.data.recipientAlias : '';

        const earliestPickup = this.props.data != null ? this.props.data.earliestPickupTime : '';
        const latestDropoff = this.props.data != null ? this.props.data.latestDropoffTime : '';

        const optionsValid = this.state.data.options.filter(x => x.valid == false).length == 0;
        const atLeastOneOption = this.state.data.options.filter(x => x.transportMethod != '').length > 0;
        const submittedToNkr = this.props.data.list.length > 0 ? this.props.data.list.filter(x => x.status == LogisticsOptionStatus.SubmittedToNkr || x.status == LogisticsOptionStatus.NkrDraft).length == this.props.data.list.length : false;
        const submittedToCenter = this.props.data.list.length > 0 ? this.props.data.list.filter(x => x.status == LogisticsOptionStatus.SubmittedToCenter).length == this.props.data.list.length : false;
        const acceptedByCenter = this.props.data.list.length > 0 ? this.props.data.list.filter(x => x.status == LogisticsOptionStatus.SelectedByCenter).length > 0 : false;
        const readonly = (this.props.audience == LogisticsAudience.Courier && (submittedToNkr || submittedToCenter || acceptedByCenter)) || (this.props.audience == LogisticsAudience.NKR && acceptedByCenter);
        const toggleType = this.getToggleType();

        return (
            <Modal bsSize='lg' backdrop='static' show={this.props.show} onHide={this.handleCancel} style={{ zIndex: 99999 }}>
                <Modal.Header closeButton style={{ backgroundColor: '#EFDFCE' }}>
                    <Modal.Title>
                        Custom Logistics - <span style={{ fontWeight: 700, color: '#133366' }}>Swap {swapNumber}</span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {this.state.dirty && (!optionsValid || !atLeastOneOption) && this.props.audience == LogisticsAudience.Courier && (
                        <Alert bsStyle='danger'>
                            <div>The following issues need to be resolved before submitting to NKR:</div>
                            <ul>
                                {!atLeastOneOption && (
                                    <li>At least one option must be completed</li>
                                )}

                                {this.renderErrors()}
                            </ul>
                        </Alert>
                    )}

                    {this.state.dirty && (!optionsValid || !atLeastOneOption || this.state.data.deadlineValid == false) && this.props.audience == LogisticsAudience.NKR && (
                        <Alert bsStyle='danger'>
                            <div>The following issues need to be resolved before submitting to the center:</div>
                            <ul>
                                {this.state.data.deadlineValid == false && (
                                    <li>The deadline must be specified</li>
                                )}

                                {!atLeastOneOption && (
                                    <li>At least one option must be completed</li>
                                )}

                                {this.renderErrors()}
                            </ul>
                        </Alert>
                    )}

                    {this.state.toggle.show == false ?
                        <>
                            <LogisticsOptionsContent
                                data={this.state.data}
                                swapNumber={swapNumber}
                                donorCenter={donorCenter}
                                donorAlias={donorAlias}
                                rdnCenterId={rdnCenterId}
                                rdnCenterAlias={rdnCenterAlias}
                                recipientCenter={recipientCenter}
                                recipientAlias={recipientAlias}
                                earliestPickup={earliestPickup}
                                latestDropoff={latestDropoff}
                                logisticsProviders={this.props.logisticsProviders}
                                transportTypes={this.props.transportTypes}
                                donorSurgeons={[]}
                                recipientSurgeons={[]}
                                validDeadline={this.state.data.deadlineValid}
                                audience={this.props.audience}
                                readonly={readonly}
                                dirty={this.state.dirty}
                                allowedOptions={this.state.allowedOptions}
                                onLogisticsProviderChange={this.handleLogisticsProviderChange}
                                onDeadlineChange={this.handleDeadlineChange}
                                onDateTimeFieldChange={this.handleDateTimeFieldChange}
                                onFieldChange={this.handleFieldChange}
                                onDropdownChange={this.handleDropdownChange}
                                onRemoveOption={this.handleRemoveOption}
                                onClearOption={this.handleClearOption}
                            />

                            <OptionNotes
                                notes={this.state.data.notes}
                                user={this.props.user}
                                readonly={readonly}
                                onAdd={this.handleAddNote}
                                onUpdate={this.handleUpdateNote}
                                onDelete={this.handleDeleteNote}
                            />

                            <div className='text-center'>* If a timezone is not set, depending on the item, the system will default to either the donor center timezone or the recipient center timezone</div>
                        </>
                        :
                        <div style={{ fontSize: 16, textAlign: 'center' }}>
                            Are you sure you want to {toggleType.copy}?
                        </div>
                    }
                </Modal.Body>

                <Modal.Footer>
                    {this.state.toggle.show == false ?
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                {this.props.audience == LogisticsAudience.NKR && (
                                    <Button className='btn-swap-logistics-plan' onClick={this.handleSwitchToSwapLogistics}>Swap Logistics</Button>
                                )}
                            </div>
                            {this.props.audience == LogisticsAudience.NKR && (
                                <div>
                                    <Button bsStyle='danger' onClick={() => this.handleToggle('delete')}>Delete</Button>
                                    <Button bsStyle='warning' onClick={() => this.handleToggle('return')}>Return to Courier</Button>
                                </div>
                            )}
                            <div style={{ display: 'flex' }}>
                                {this.props.audience == LogisticsAudience.Courier && !submittedToNkr && !submittedToCenter && !acceptedByCenter && (
                                    <>
                                        <Button bsStyle='primary' onClick={() => this.handleSave(SaveType.Submit)}>Submit Options to NKR</Button>
                                        <Button bsStyle='primary' onClick={() => this.handleSave(SaveType.Draft)}>Save Draft</Button>
                                        <Button onClick={this.handleCancel}>Cancel</Button>
                                    </>
                                )}

                                {this.props.audience == LogisticsAudience.Courier && (submittedToNkr || submittedToCenter || acceptedByCenter) && (
                                    <Button bsStyle='primary' onClick={this.handleCancel}>Close</Button>
                                )}

                                {this.props.audience == LogisticsAudience.NKR && !acceptedByCenter && (
                                    <>
                                        {!submittedToCenter && (
                                            <Button bsStyle='primary' onClick={() => this.handleSave(SaveType.Submit)}>Submit Options to Center</Button>
                                        )}

                                        {submittedToCenter && (
                                            <Button bsStyle='primary' onClick={() => this.handleSave(SaveType.Return)}>Resubmit Options to Center</Button>
                                        )}

                                        <Button bsStyle='primary' onClick={() => this.handleSave(SaveType.Draft)}>Save Draft</Button>
                                        <Button onClick={this.handleCancel}>Cancel</Button>
                                    </>
                                )}

                                {this.props.audience == LogisticsAudience.NKR && acceptedByCenter && (
                                    <Button bsStyle='primary' onClick={this.handleCancel}>Close</Button>
                                )}
                            </div>
                        </div>
                        :
                        <>
                            <Button bsStyle={this.state.toggle.type == 'delete' ? 'danger' : 'warning'} onClick={this.handleProcessToggle}>{toggleType.label}</Button>
                            <Button onClick={this.handleCancelToggle}>No, Cancel</Button>
                        </>
                    }
                </Modal.Footer>
            </Modal>
        );
    }
}

export default LogisticsOption;
